/**
 * useTextInsertion Hook
 * 
 * Handles text insertion from external sources (chat, AI, etc.)
 */

import { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { renderToStaticMarkup } from 'react-dom/server';
import { classifyInsertion } from '../../../lib/table-parser';
import { sanitizeTableHtml } from '../../../lib/officeHtmlSanitizer';

/**
 * Hook to handle text insertion from custom events
 * 
 * Args:
 *   editor (object): TipTap editor instance
 *   setPastedText (function): State setter for pasted text
 *   setDetectedFormat (function): State setter for detected format
 *   setConfirmingPaste (function): State setter for paste confirmation dialog
 *   setPastePosition (function): State setter for cursor position before paste dialog
 *
 * Returns:
 *   void
 */
export const useTextInsertion = (editor, setPastedText, setDetectedFormat, setConfirmingPaste, setPastePosition, setPastedPlainText) => {
  useEffect(() => {
    if (!editor) return;

    /**
     * Centralized function to handle text insertion from any source
     * 
     * Args:
     *   text (string): The text to insert
     * 
     * Returns:
     *   boolean: True if insertion was handled
     */
    const handleTextInsertion = (text) => {
      const trimmedText = text.trim();

      // Reason (T-018): ONE shared classifier drives Insert AND Replace so they can
      // never diverge. It only says "table" for genuinely tabular text; prose (even
      // with commas) → plaintext, and a prose+table message → markdown (rendered to
      // paragraphs + a native table) instead of being shredded into a 1×N table.
      const { action, format } = classifyInsertion(trimmedText);

      if (action === 'table') {
        // Reason: Show confirmation dialog for table insertion from non-HTML sources
        setPastedText(trimmedText);
        if (setPastedPlainText) setPastedPlainText(trimmedText);
        setDetectedFormat(format);
        // Reason: Save cursor position before showing dialog so confirm/cancel handlers
        // can insert content at the correct location
        if (setPastePosition) {
          setPastePosition(editor.state.selection.from);
        }
        setConfirmingPaste(true);
        return true; // Indicate that we've handled the insertion
      }

      // Reason: HTML table (e.g. the chat "+"-button sends table.outerHTML). Run
      // it through the SAME officeHtmlSanitizer as the paste path — strips MSO
      // cruft and wrapper markup, keeps text + merges — so TipTap parses it into
      // NATIVE table nodes, the ONE canonical representation for every path.
      if (action === 'html-table') {
        const cleanTableHtml = sanitizeTableHtml(trimmedText);
        if (cleanTableHtml) {
          editor.commands.insertContent(cleanTableHtml, {
            parseOptions: { preserveWhitespace: false },
          });
          return true;
        }
        // Sanitizer produced nothing usable — fall through to raw-HTML insertion.
      }

      // Reason: Other HTML markup (or an html-table whose sanitizer returned
      // nothing but which still starts with '<') — insert as-is.
      if (action === 'raw-html' || (action === 'html-table' && trimmedText.startsWith('<'))) {
        editor.commands.insertContent(trimmedText);
        return true;
      }

      // Markdown (incl. prose+table mixes): render to HTML so surrounding prose
      // becomes paragraphs and any table becomes a native table node.
      if (action === 'markdown') {
        const html = renderToStaticMarkup(
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{trimmedText}</ReactMarkdown>
        );
        editor.commands.insertContent(html, {
          parseOptions: {
            preserveWhitespace: false,
          },
        });
        return true; // We've handled it
      }

      // If it's not a table and not markdown, insert as plain text
      editor.commands.insertContent(trimmedText);
      return true;
    };

    const handleInsertText = (event) => {
      const { text, savedSelection } = event.detail;

      editor.commands.focus();

      if (savedSelection) {
        try {
          editor.commands.setTextSelection(savedSelection.to);
          editor.commands.insertContent('<p></p>');
        } catch (error) {
          console.error('Error using saved selection for insert:', error);
        }
      } else {
        const { from, to } = editor.state.selection;
        if (from !== to) editor.commands.setTextSelection(to);
      }

      handleTextInsertion(text);
    };

    const handleReplaceText = (event) => {
      const { newText, savedSelection } = event.detail;
      editor.commands.focus();

      if (savedSelection) {
        try {
          editor.commands.setTextSelection({ from: savedSelection.from, to: savedSelection.to });
        } catch (error) {
          console.error('Error using saved selection for replace:', error);
        }
      }

      editor.commands.deleteSelection();
      handleTextInsertion(newText);
    };

    window.addEventListener('insertText', handleInsertText);
    window.addEventListener('replaceText', handleReplaceText);

    return () => {
      window.removeEventListener('insertText', handleInsertText);
      window.removeEventListener('replaceText', handleReplaceText);
    };
  }, [editor, setPastedText, setDetectedFormat, setConfirmingPaste, setPastePosition, setPastedPlainText]);
};

